import { Fragment } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Markdown da resposta do Brain, montado como elementos React.
 *
 * **Nunca HTML.** O texto vem de um modelo de linguagem, que repete o que leu —
 * inclusive um `<script>` escondido num PDF anexado. Aqui cada pedaço vira nó
 * de texto ou elemento criado por nós, então o React escapa tudo e não existe
 * `dangerouslySetInnerHTML` para escapar dele. Link só com `http`, `https` ou
 * `mailto`: `javascript:` vira texto.
 *
 * Cobre o que o assistente usa: parágrafo, título, lista, citação, código,
 * tabela, régua, negrito, itálico, código em linha e link.
 */

export type BlocoDoMarkdown =
    | { tipo: 'paragrafo'; linhas: string[] }
    | { tipo: 'titulo'; nivel: number; texto: string }
    | { tipo: 'lista'; ordenada: boolean; inicio: number; itens: string[] }
    | { tipo: 'codigo'; texto: string }
    | { tipo: 'citacao'; linhas: string[] }
    | { tipo: 'tabela'; cabecalho: string[]; linhas: string[][] }
    | { tipo: 'regua' };

const CERCA = /^\s{0,3}(```|~~~)/;
const TITULO = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const ITEM = /^\s{0,3}[-*+]\s+(.*)$/;
const ITEM_NUMERADO = /^\s{0,3}(\d{1,9})[.)]\s+(.*)$/;
const CITACAO = /^\s{0,3}>\s?(.*)$/;
const REGUA = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const LINHA_DE_TABELA = /^\s*\|.*\|\s*$/;
const SEPARADOR_DE_TABELA = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

function celulas(linha: string): string[] {
    return linha.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

function comecaBloco(linha: string, seguinte: string | undefined): boolean {
    return CERCA.test(linha) || TITULO.test(linha) || REGUA.test(linha) || ITEM.test(linha)
        || ITEM_NUMERADO.test(linha) || CITACAO.test(linha)
        || (LINHA_DE_TABELA.test(linha) && SEPARADOR_DE_TABELA.test(seguinte ?? ''));
}

export function blocosDoMarkdown(markdown: string): BlocoDoMarkdown[] {
    const linhas = markdown.replace(/\r\n?/g, '\n').split('\n');
    const blocos: BlocoDoMarkdown[] = [];
    let i = 0;

    while (i < linhas.length) {
        const linha = linhas[i] ?? '';
        if (!linha.trim()) {
            i += 1;
            continue;
        }

        const cerca = CERCA.exec(linha);
        if (cerca) {
            const marca = cerca[1] ?? '```';
            const corpo: string[] = [];
            i += 1;
            while (i < linhas.length && !(linhas[i] ?? '').trim().startsWith(marca)) {
                corpo.push(linhas[i] ?? '');
                i += 1;
            }
            i += 1;
            blocos.push({ tipo: 'codigo', texto: corpo.join('\n') });
            continue;
        }

        const titulo = TITULO.exec(linha);
        if (titulo) {
            blocos.push({ tipo: 'titulo', nivel: (titulo[1] ?? '#').length, texto: titulo[2] ?? '' });
            i += 1;
            continue;
        }

        if (REGUA.test(linha)) {
            blocos.push({ tipo: 'regua' });
            i += 1;
            continue;
        }

        if (LINHA_DE_TABELA.test(linha) && SEPARADOR_DE_TABELA.test(linhas[i + 1] ?? '')) {
            const cabecalho = celulas(linha);
            const corpo: string[][] = [];
            i += 2;
            while (i < linhas.length && LINHA_DE_TABELA.test(linhas[i] ?? '')) {
                corpo.push(celulas(linhas[i] ?? ''));
                i += 1;
            }
            blocos.push({ tipo: 'tabela', cabecalho, linhas: corpo });
            continue;
        }

        const numerado = ITEM_NUMERADO.exec(linha);
        if (ITEM.test(linha) || numerado) {
            const ordenada = Boolean(numerado);
            const padrao = ordenada ? ITEM_NUMERADO : ITEM;
            const itens: string[] = [];
            while (i < linhas.length) {
                const atual = linhas[i] ?? '';
                const item = padrao.exec(atual);
                if (item) {
                    itens.push((ordenada ? item[2] : item[1]) ?? '');
                } else if (atual.trim() && itens.length > 0 && !comecaBloco(atual, linhas[i + 1])) {
                    /* Linha de continuação do item anterior. */
                    itens[itens.length - 1] = `${itens[itens.length - 1] ?? ''} ${atual.trim()}`;
                } else {
                    break;
                }
                i += 1;
            }
            blocos.push({ tipo: 'lista', ordenada, inicio: Number(numerado?.[1] ?? 1), itens });
            continue;
        }

        if (CITACAO.test(linha)) {
            const citadas: string[] = [];
            while (i < linhas.length) {
                const citada = CITACAO.exec(linhas[i] ?? '');
                if (!citada) break;
                citadas.push(citada[1] ?? '');
                i += 1;
            }
            blocos.push({ tipo: 'citacao', linhas: citadas });
            continue;
        }

        const paragrafo: string[] = [];
        while (i < linhas.length && (linhas[i] ?? '').trim() && (paragrafo.length === 0 || !comecaBloco(linhas[i] ?? '', linhas[i + 1]))) {
            paragrafo.push(linhas[i] ?? '');
            i += 1;
        }
        blocos.push({ tipo: 'paragrafo', linhas: paragrafo });
    }

    return blocos;
}

const PROTOCOLOS_PERMITIDOS = ['http:', 'https:', 'mailto:'];

/** O endereço normalizado, ou `null` se o protocolo não é permitido ou o endereço não é absoluto. */
export function enderecoSeguro(href: string): string | null {
    try {
        const url = new URL(href);
        return PROTOCOLOS_PERMITIDOS.includes(url.protocol) ? url.href : null;
    } catch {
        /* Endereço que não se interpreta não vira link: fica como texto. */
        return null;
    }
}

const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+?\*\*|__[^_\n]+?__)|(\[[^\]\n]+\]\([^)\s]+\))|(https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)])|(\*[^*\s](?:[^*\n]*?[^*\s])?\*)|((?<![\p{L}\p{N}])_[^_\s](?:[^_\n]*?[^_\s])?_(?![\p{L}\p{N}]))/gu;

const ESTILO_DO_CODIGO: CSSProperties = {
    fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 13,
    background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-xs)', padding: '0 4px', overflowWrap: 'anywhere',
};

function Link({ href, children }: { href: string; children: ReactNode }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link)', overflowWrap: 'anywhere' }}>
            {children}
        </a>
    );
}

function emLinha(texto: string, chave: string): ReactNode[] {
    const nos: ReactNode[] = [];
    let ultimo = 0;
    let n = 0;
    for (const achado of texto.matchAll(INLINE)) {
        const inicio = achado.index ?? 0;
        const bruto = achado[0];
        if (inicio > ultimo) nos.push(texto.slice(ultimo, inicio));
        const k = `${chave}.${n}`;
        n += 1;
        const [, codigo, negrito, link, endereco, italico, italicoSublinhado] = achado;

        if (codigo) {
            nos.push(<code key={k} style={ESTILO_DO_CODIGO}>{codigo.slice(1, -1)}</code>);
        } else if (negrito) {
            nos.push(<strong key={k}>{emLinha(negrito.slice(2, -2), k)}</strong>);
        } else if (link) {
            const meio = link.indexOf('](');
            const rotulo = link.slice(1, meio);
            const seguro = enderecoSeguro(link.slice(meio + 2, -1));
            nos.push(seguro
                ? <Link key={k} href={seguro}>{emLinha(rotulo, k)}</Link>
                : <Fragment key={k}>{rotulo}</Fragment>);
        } else if (endereco) {
            const seguro = enderecoSeguro(endereco);
            nos.push(seguro ? <Link key={k} href={seguro}>{endereco}</Link> : <Fragment key={k}>{endereco}</Fragment>);
        } else {
            const enfase = italico ?? italicoSublinhado ?? bruto;
            nos.push(<em key={k}>{emLinha(enfase.slice(1, -1), k)}</em>);
        }
        ultimo = inicio + bruto.length;
    }
    if (ultimo < texto.length) nos.push(texto.slice(ultimo));
    return nos;
}

const PARAGRAFO: CSSProperties = { margin: 0, fontSize: 14, lineHeight: '22px', color: 'var(--text-primary)', overflowWrap: 'anywhere' };

function renderizar(bloco: BlocoDoMarkdown, k: string): ReactNode {
    switch (bloco.tipo) {
        case 'paragrafo':
            return (
                <p key={k} style={PARAGRAFO}>
                    {bloco.linhas.map((linha, j) => (
                        <Fragment key={j}>
                            {j > 0 && <br />}
                            {emLinha(linha, `${k}.${j}`)}
                        </Fragment>
                    ))}
                </p>
            );
        case 'titulo':
            return (
                <p key={k} style={{ ...PARAGRAFO, fontWeight: 600, fontSize: bloco.nivel <= 2 ? 16 : 14 }}>
                    {emLinha(bloco.texto, k)}
                </p>
            );
        case 'lista': {
            const itens = bloco.itens.map((item, j) => <li key={j} style={{ marginBlock: 2 }}>{emLinha(item, `${k}.${j}`)}</li>);
            const estilo: CSSProperties = { ...PARAGRAFO, paddingLeft: 22 };
            return bloco.ordenada
                ? <ol key={k} start={bloco.inicio} style={estilo}>{itens}</ol>
                : <ul key={k} style={estilo}>{itens}</ul>;
        }
        case 'codigo':
            return (
                <pre
                    key={k}
                    style={{
                        margin: 0, padding: 12, overflowX: 'auto', maxWidth: '100%',
                        background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)',
                        borderRadius: 'var(--radius-sm)', fontSize: 13, lineHeight: '20px',
                        fontFamily: 'var(--font-mono, ui-monospace, monospace)', color: 'var(--text-primary)',
                    }}
                >
                    <code>{bloco.texto}</code>
                </pre>
            );
        case 'citacao':
            return (
                <blockquote
                    key={k}
                    style={{ ...PARAGRAFO, paddingLeft: 12, borderLeft: '3px solid var(--border-strong)', color: 'var(--text-secondary)' }}
                >
                    {bloco.linhas.map((linha, j) => (
                        <Fragment key={j}>
                            {j > 0 && <br />}
                            {emLinha(linha, `${k}.${j}`)}
                        </Fragment>
                    ))}
                </blockquote>
            );
        case 'tabela':
            return (
                <div key={k} style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 13, color: 'var(--text-primary)', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                {bloco.cabecalho.map((c, j) => (
                                    <th key={j} scope="col" style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--border-strong)', fontWeight: 600 }}>
                                        {emLinha(c, `${k}.h${j}`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {bloco.linhas.map((linha, j) => (
                                <tr key={j}>
                                    {bloco.cabecalho.map((_, c) => (
                                        <td key={c} style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' }}>
                                            {emLinha(linha[c] ?? '', `${k}.${j}.${c}`)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        case 'regua':
            return <hr key={k} style={{ border: 0, borderTop: '1px solid var(--border)', margin: 0, width: '100%' }} />;
    }
}

export interface MarkdownSeguroProps {
    texto: string;
}

export function MarkdownSeguro({ texto }: MarkdownSeguroProps) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {blocosDoMarkdown(texto).map((bloco, i) => renderizar(bloco, `b${i}`))}
        </div>
    );
}
